<template>
  <div class="invoice-print-wrapper p-2 p-md-4 bg-white min-vh-100">
    <div class="d-flex justify-content-end align-items-center mb-2 print-hide">
      <div>
        <b-button variant="outline-success" class="mr-1" @click="exportExcel" :disabled="loading || reportData.length === 0">
          <feather-icon icon="DownloadIcon" class="mr-50" />
          Export Excel
        </b-button>
        <b-button variant="outline-secondary" class="mr-1" @click="$router.go(-1)">
          Back
        </b-button>
        <b-button variant="primary" @click="printReport" :disabled="loading">
          <feather-icon icon="PrinterIcon" class="mr-50" />
          Print Report
        </b-button>
      </div>
    </div>

    <!-- The actual print area -->
    <div class="print-area">
      <div class="text-center mb-2">
        <h4 class="font-weight-bold mb-1" v-if="businessName">{{ businessName }}</h4>
        <h5 class="font-weight-bold mb-1">SALES INVOICE MONTHLY SUMMARY REPORT</h5>
        <h6 class="text-muted">Tax Period: {{ monthName }} {{ year }}</h6>
      </div>

      <div class="table-responsive">
        <table ref="refSummaryTable" class="table table-bordered table-sm summary-table" style="font-size: 0.75rem;">
          <thead class="text-center table-active">
            <tr>
              <th rowspan="2" class="align-middle">Sr No.</th>
              <th colspan="3">Particulars of Buyer</th>
              <th rowspan="2" class="align-middle">Sale<br>Origination<br>Province</th>
              <th rowspan="2" class="align-middle">Destination<br>Province</th>
              <th colspan="6">Particulars of Document</th>
              <th colspan="9">Invoice Details</th>
              <th colspan="2">Exemption, Zero &<br>Reduce Rated Reference</th>
            </tr>
            <tr>
              <th>Registration No.</th>
              <th>Name</th>
              <th>Type</th>
              <th>Invoice Type</th>
              <th>Invoice No.</th>
              <th>Sales Tax<br>Invoice No.</th>
              <th>Invoice Date</th>
              <th>HS Code</th>
              <th>Sale Type</th>
              <th>Rate</th>
              <th>UOM</th>
              <th>Quantity</th>
              <th>Value of Sales<br>Excl. ST</th>
              <th>Fixed /<br>notified</th>
              <th>Sales Tax/<br>FED in ST</th>
              <th>ST withheld<br>as WH</th>
              <th>Extra Tax</th>
              <th>Further<br>Tax</th>
              <th>SRO<br>No./Schedule</th>
              <th>Item Sr. No.</th>
            </tr>
          </thead>
          <tbody>
             <template v-if="loading">
               <tr><td colspan="23" class="text-center p-3"><b-spinner label="Loading..." /></td></tr>
             </template>
             <template v-else-if="reportData.length === 0">
               <tr><td colspan="23" class="text-center p-3 text-muted">No records found for {{ monthName }} {{ year }}</td></tr>
             </template>
             <template v-else>
              <tr v-for="(item, index) in reportData" :key="index" class="text-center">
                <td>{{ index + 1 }}</td>
                <td>{{ item.registrationNo || '-' }}</td>
                <td class="text-left" style="min-width: 150px;">{{ item.name || '-' }}</td>
                <td>{{ item.type || '-' }}</td>
                <td>{{ businessProvince  || '-' }}</td>
                <td>{{ item.destinationProvince || '-' }}</td>
                <td>{{ item.invoiceType || '-' }}</td>
                <td>{{ item.invoiceNo || '-' }}</td>
                <td style="max-width: 130px; word-wrap: break-word; word-break: break-all; white-space: normal;">{{ item.FbrInvoiceNo || item.fbrInvoiceNo || item.salesTaxInvoiceNo || '-' }}</td>
                <td>{{ formatDate(item.invoiceDate) || '-' }}</td>
                <td>{{ item.hsCode || '-' }}</td>
                <td class="text-left" style="min-width: 150px;">{{ item.saleType || '-' }}</td>
                <td>{{ item.rate ? item.rate + '%' : '-' }}</td>
                <td>{{ item.uom || '-' }}</td>
                <td class="text-right">{{ formatNumber(item.quantity) }}</td>
                <td class="text-right">{{ formatNumber(item.valueOfSalesExclST) }}</td>
                <td class="text-right">{{ formatNumber(item.fixedNotified) }}</td>
                <td class="text-right">{{ formatNumber(item.salesTaxFEDinST) }}</td>
                <td class="text-right">{{ formatNumber(item.stWithheldAsWH) }}</td>
                <td class="text-right">{{ formatNumber(item.extraTax) }}</td>
                <td class="text-right">{{ formatNumber(item.furtherTax) }}</td>
                <td>{{ item.sroNoSchedule || '0' }}</td>
                <td>{{ item.itemSrNo || '0' }}</td>
              </tr>
              <tr class="font-weight-bold" style="background-color: #e2e8f0; -webkit-print-color-adjust: exact; print-color-adjust: exact;">
                <td colspan="14" class="text-right pr-2">Total Sales</td>
                <td class="text-right">{{ formatNumber(totalQuantity) }}</td>
                <td class="text-right">{{ formatNumber(totalValueOfSales) }}</td>
                <td class="text-right">{{ formatNumber(totalFixedNotified) }}</td>
                <td class="text-right">{{ formatNumber(totalSalesTax) }}</td>
                <td class="text-right">{{ formatNumber(totalStWithheld) }}</td>
                <td class="text-right">{{ formatNumber(totalExtraTax) }}</td>
                <td class="text-right">{{ formatNumber(totalFurtherTax) }}</td>
                <td colspan="2"></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue';
import { ref, onMounted, onUnmounted, computed } from '@vue/composition-api';
import * as XLSX from 'xlsx';
import store from '@/store';
import router from '@/router';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import moment from 'moment';

import invoiceStoreModule from '../invoiceStoreModule';
import BusinessStoreModule from '../../dashboard/business/BusinessStoreModule';

export default {
  components: {
    BButton,
    BSpinner,
  },
  setup() {
    const toast = useToast();
    const loading = ref(true);
    const reportData = ref([]);

    // Attempt to pull business name from store or localStorage
    const localUser = JSON.parse(localStorage.getItem('userData') || '{}');
    const businessName = ref(store.state.user?.user?.name || localUser?.name || 'Business Name');

    const INVOICE_APP_STORE_MODULE_NAME = 'sales-invoice';
    const BUSINESS_APP_STORE_MODULE_NAME = 'business';

    // Register module
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) {
      store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule);
    }
    if (!store.hasModule(BUSINESS_APP_STORE_MODULE_NAME)) {
      store.registerModule(BUSINESS_APP_STORE_MODULE_NAME, BusinessStoreModule);
    }

    // UnRegister on leave
    onUnmounted(() => {
      // Actually don't unregister if it's used elsewhere, but this is the pattern used in the app
      if (store.hasModule(INVOICE_APP_STORE_MODULE_NAME))
        store.unregisterModule(INVOICE_APP_STORE_MODULE_NAME);
      if (store.hasModule(BUSINESS_APP_STORE_MODULE_NAME))
        store.unregisterModule(BUSINESS_APP_STORE_MODULE_NAME);
    });

    const routeParams = router.currentRoute.query;
    const monthName = ref(routeParams.month || '');
    const year = ref(routeParams.year || '');

    const businessProvince = ref('');
    const fetchReport = () => {
      loading.value = true;
      store.dispatch('sales-invoice/fetchMonthlySummaryReport', {
        monthName: monthName.value,
        year: year.value
      }).then((data) => {
        reportData.value = data || [];
      }).catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.message || error || 'Failed to fetch report',
          },
        });
      }).finally(() => {
        loading.value = false;
      });
    };

    onMounted(() => {
      if(monthName.value && year.value) {
        fetchReport();
      } else {
         loading.value = false;
         toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'Month and Year parameters are missing',
          },
        });
      }

      // Fetch accurate business details
      store.dispatch('business/fetchBusinessDetails')
        .then((response) => {
          if (response) {
            if (response.name) businessName.value = response.name;
            if (response.province) businessProvince.value = response.province;
          }
        })
        .catch(() => {
          // Failure to fetch can silently fallback to local variables
        });
    });

    const totalQuantity = computed(() => reportData.value.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0));
    const totalValueOfSales = computed(() => reportData.value.reduce((sum, item) => sum + (parseFloat(item.valueOfSalesExclST) || 0), 0));
    const totalFixedNotified = computed(() => reportData.value.reduce((sum, item) => sum + (parseFloat(item.fixedNotified) || 0), 0));
    const totalSalesTax = computed(() => reportData.value.reduce((sum, item) => sum + (parseFloat(item.salesTaxFEDinST) || 0), 0));
    const totalStWithheld = computed(() => reportData.value.reduce((sum, item) => sum + (parseFloat(item.stWithheldAsWH) || 0), 0));
    const totalExtraTax = computed(() => reportData.value.reduce((sum, item) => sum + (parseFloat(item.extraTax) || 0), 0));
    const totalFurtherTax = computed(() => reportData.value.reduce((sum, item) => sum + (parseFloat(item.furtherTax) || 0), 0));

    const printReport = () => {
      window.print();
    };

    const refSummaryTable = ref(null);

    const exportExcel = () => {
      if (!refSummaryTable.value) return;
      
      const wb = XLSX.utils.table_to_book(refSummaryTable.value, { sheet: "Monthly Summary", raw: true });
      XLSX.writeFile(wb, `Sales_Invoice_Monthly_Summary_${monthName.value}_${year.value}.xlsx`);
    };

    const formatNumber = (val) => {
      if (val === null || val === undefined) return '0';
      return Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      return moment(dateStr).format('DD-MMM-YYYY');
    };

    return {
      loading,
      reportData,
      monthName,
      year,
      printReport,
      exportExcel,
      refSummaryTable,
      formatNumber,
      formatDate,
      businessName,
      businessProvince,
      totalQuantity,
      totalValueOfSales,
      totalFixedNotified,
      totalSalesTax,
      totalStWithheld,
      totalExtraTax,
      totalFurtherTax,
    };
  }
};
</script>

<style lang="scss">
@media print {
  body * {
    visibility: hidden;
  }
  .invoice-print-wrapper, .invoice-print-wrapper * {
    visibility: visible;
  }
  .invoice-print-wrapper {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background-color: transparent !important;
    padding: 10mm !important; /* give print margins */
  }
  .print-hide {
    display: none !important;
  }
  @page {
    size: A3 landscape;
    margin: 5mm; /* hides default browser URL and Date strings */
  }
  .summary-table th {
    background-color: #f7f7f7 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .summary-table td, .summary-table th {
    padding: 0.15rem !important; /* Compact spacing */
    font-size: 0.65rem !important;
  }
}

.summary-table {
  width: 100%;
  th {
    background-color: #f7f7f7;
    border: 1px solid #dee2e6;
  }
  td {
    border: 1px solid #dee2e6;
    vertical-align: middle;
  }
}
</style>
